import type {
  Offering,
  RankingOfferingsPayload,
  RankingOfferingsResponse,
} from '@highereducation/offerings';
import { prefixHenSitePath } from '@highereducation/request-context';

const DEFAULT_URL = prefixHenSitePath('/api/offerings/ranking/');

interface FetchSponsoredOfferingsOptions {
  /**
   * Override the proxy URL. Useful for the dev preview's `?demo=1` toggle so
   * it can exercise the same proxy + transform path as the real branch.
   */
  urlOverride?: string;
}

/**
 * Fetches sponsored offerings through the hen-site Next.js proxy. Returns an
 * empty array on any failure so callers (RankingsAdapter, the dev preview)
 * can degrade to organic-only rendering. Errors are reported to New Relic
 * via `window.newrelic?.noticeError` when the browser agent is loaded, so
 * silent degradation still surfaces in alerting.
 */
export async function fetchSponsoredOfferings(
  payload: RankingOfferingsPayload,
  signal?: AbortSignal,
  options: FetchSponsoredOfferingsOptions = {},
): Promise<Offering[]> {
  const url = options.urlOverride ?? DEFAULT_URL;

  try {
    // Trailing slash avoids a 308 redirect under Next's `trailingSlash: true`.
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal,
    });

    if (!res.ok) {
      const err = new Error(`fetchSponsoredOfferings non-2xx (${res.status})`);
      console.error(err.message);
      reportToNewRelic(err, { url, status: res.status });
      return [];
    }

    const json = (await res.json()) as RankingOfferingsResponse;
    return json.data ?? [];
  } catch (err) {
    if ((err as Error)?.name === 'AbortError') return [];
    console.error('fetchSponsoredOfferings failed', err);
    reportToNewRelic(err, { url });
    return [];
  }
}

function reportToNewRelic(
  err: unknown,
  attrs: Record<string, string | number>,
): void {
  if (typeof window === 'undefined') return;
  const nr = (window as unknown as { newrelic?: { noticeError?: Function } })
    .newrelic;
  nr?.noticeError?.(err instanceof Error ? err : new Error(String(err)), attrs);
}
